// src/utils/proposalTable.js
//
// The §1.4 table renderer — "the universal coverage primitive" per
// proposal_payload.schema.json $defs/table (schema now on sharepoint-mcp). A Schedule of Hazards, a Limits
// block, a Storage Locations list and a Coverage Limitations grid are all the
// same object; this module does not interpret content.
//
// Measure/draw are split deliberately. §1.4 rule 9 — "A table never splits
// across a page with fewer than two rows on either side. Move the whole table
// if it would orphan." — is undecidable with a single-row lookahead, so
// measureTable computes the whole layout, including every legal split point,
// before a single mark is made.
//
// All text is measured and drawn through pdfText. widthOfTextAtSize is never
// called here: pdf-lib measures kerned and renders unkerned, so it is the
// wrong basis (see the note on measureTracked).

import { measureTracked, drawTracked, wrapTracked, applyTransform } from './pdfText';
import {
  CONTENT_WIDTH,
  CONTENT_FLOOR,
  MARGIN_LEFT,
  MIN_ROW_HEIGHT,
  BODY_LEADING,
  BASELINE,
  TYPE,
} from './proposalLayout';

/** Em dash for empty cells (§1.4 rule 5). pdfText keeps its own private copy. */
const EM_DASH = '—';

/** Rule weights, §1.4 rule 2. These three are the ONLY marks a table makes. */
const HEADER_RULE_THICKNESS = 0.75;
const CLOSING_RULE_THICKNESS = 0.75;
const HAIRLINE_THICKNESS = 0.25;

/** §1.4 rule 8: table title above the table with 6 pt of air beneath. */
const TITLE_GAP = 6;

/**
 * Whitespace reserved BETWEEN columns. §1.4 rule 1 forbids vertical rules —
 * "Columns are separated by whitespace and alignment" — but gives no figure.
 * 12 pt is this module's choice. Not a spec value.
 *
 * This is reserved out of the column width budget before any normalization:
 * n columns get n-1 gutters, so the widths themselves sum to
 * CONTENT_WIDTH - GUTTER * (n - 1) and x steps by width + GUTTER. Folding the
 * gutter into each column's natural width instead (the original approach) left
 * the widths summing to the full 492 with no space between them, so a
 * right-aligned column sat flush against the next left-aligned one — rendering
 * "Class CodePremium Basis" and "91341Gross Sales".
 */
export const COLUMN_GUTTER = 12;

/**
 * Drop from a row's top edge to its first text baseline. Centres a 9 pt line
 * in the 18 pt minimum row of §1.3. Not a spec value.
 */
const TEXT_INSET = 12;

const RIGHT_EDGE = MARGIN_LEFT + CONTENT_WIDTH;

/**
 * Display text for a cell, per §1.4 rule 5: "Empty cells render `—` (em dash),
 * never blank. A blank cell reads as an error."
 *
 * The schema types a cell as a plain string ("Display text"), and the table
 * description states "The renderer does not interpret content" — so currency is
 * NOT reformatted here. Payloads arrive pre-formatted and the §4 validator
 * checks currency columns against ^\$[\d,]+\.\d{2}$. null/undefined are handled
 * defensively even though the schema does not permit them.
 */
export function cellText(raw) {
  if (raw === null || raw === undefined) return EM_DASH;
  const s = String(raw);
  return s.trim() === '' ? EM_DASH : s;
}

/**
 * Legal split points for a body of `n` rows, per §1.4 rule 9.
 *
 * Returns the counts of rows that may remain on the first fragment. Both sides
 * need at least two rows, so k >= 2 and n - k >= 2. Fewer than four rows has no
 * legal split at all — such a table can only move whole.
 */
export function legalSplitCounts(n) {
  const out = [];
  for (let k = 2; k <= n - 2; k++) out.push(k);
  return out;
}

/** Height of a fragment: `count` rows starting at `start`, title optional. */
export function fragmentHeight(plan, start, count, withTitle) {
  let h = withTitle ? plan.titleHeight : 0;
  h += plan.headerHeight + HEADER_RULE_THICKNESS;
  for (let i = 0; i < count; i++) h += plan.rowHeights[start + i];
  h += Math.max(0, count - 1) * HAIRLINE_THICKNESS;
  h += CLOSING_RULE_THICKNESS;
  return h;
}

/**
 * Width available to the columns themselves, once the n-1 inter-column gutters
 * are reserved. 492 for one column, 480 for two, 456 for four, 420 for seven.
 */
export function availableWidth(nCols) {
  return CONTENT_WIDTH - COLUMN_GUTTER * Math.max(0, nCols - 1);
}

/**
 * Resolve column widths to exactly availableWidth(n) — NOT to CONTENT_WIDTH.
 * The gutters live outside these numbers; x steps by width + COLUMN_GUTTER, so
 * the last column's right edge still lands on MARGIN_LEFT + CONTENT_WIDTH.
 *
 * width_hint is a relative weight where present; null means measure the
 * content. Mixed tables give unhinted columns their measured natural width and
 * split what is left among the hinted ones by weight.
 */
function resolveWidths(naturals, hints, warnings) {
  const n = naturals.length;
  const available = availableWidth(n);
  const hinted = hints.map((h) => typeof h === 'number' && Number.isFinite(h) && h > 0);
  const anyHinted = hinted.some(Boolean);
  const allHinted = hinted.every(Boolean);

  let widths;

  if (!anyHinted) {
    const sum = naturals.reduce((a, b) => a + b, 0);
    widths = naturals.map((w) => (sum > 0 ? (w / sum) * available : available / n));
  } else if (allHinted) {
    const sum = hints.reduce((a, b) => a + b, 0);
    widths = hints.map((h) => (h / sum) * available);
  } else {
    const naturalSum = naturals.reduce((a, w, i) => (hinted[i] ? a : a + w), 0);
    const remaining = available - naturalSum;
    if (remaining > 0) {
      const hintSum = hints.reduce((a, h, i) => (hinted[i] ? a + h : a), 0);
      widths = naturals.map((w, i) => (hinted[i] ? (hints[i] / hintSum) * remaining : w));
    } else {
      // Measured columns alone already fill the available width; there is
      // nothing left to apportion, so fall back to pure measurement throughout.
      warnings.push(
        `width_hint ignored: measured columns already consume the available ${available} pt`,
      );
      const sum = naturals.reduce((a, b) => a + b, 0);
      widths = naturals.map((w) => (sum > 0 ? (w / sum) * available : available / n));
    }
  }

  // Force the total onto availableWidth exactly rather than a float near it, so
  // that the final right edge lands on MARGIN_LEFT + CONTENT_WIDTH.
  const head = widths.slice(0, -1);
  const headSum = head.reduce((a, b) => a + b, 0);
  return [...head, available - headSum];
}

/**
 * Compute the full layout plan for a table, before anything is drawn.
 *
 * @param {Object} ctx proposal context from createProposalContext
 * @param {Object} table $defs/table — { title, columns, rows }
 * @returns {Object} plan
 */
export function measureTable(ctx, table) {
  const warnings = [];
  const titleSpec = TYPE.tableTitle;
  const headSpec = TYPE.tableHeaderCell;
  const bodySpec = TYPE.tableBodyCell;

  const columns = table.columns.map((c) => ({
    label: c.label,
    align: c.align,
    format: c.format || 'text',
    widthHint: c.width_hint === undefined ? null : c.width_hint,
    // Optional per-column BODY font size (header keeps TYPE.tableHeaderCell).
    // Not a §1.4 value; undefined means the spec size, so proposals are
    // unchanged. The handoff PDF uses it to keep identifiers on one line.
    fontSize: Number.isFinite(c.fontSize) && c.fontSize > 0 ? c.fontSize : undefined,
  }));
  const nCols = columns.length;

  // §4 enforces this, but a mismatch here would silently drop or shift cells.
  table.rows.forEach((row, i) => {
    if (row.length !== nCols) {
      warnings.push(`row ${i} has ${row.length} cells, expected ${nCols}`);
    }
  });

  // §1.4 rule 4: numeric columns right, label columns left, never centred.
  columns.forEach((c, i) => {
    if (c.format !== 'text' && c.align !== 'right') {
      warnings.push(`column ${i} (${c.format}) is ${c.align}-aligned; rule 4 wants right`);
    }
  });

  // Display text, resolved once so measure and draw cannot disagree.
  const headerDisplay = columns.map((c) => applyTransform(c.label, headSpec.transform));
  const bodyDisplay = table.rows.map((row) =>
    columns.map((_, ci) => cellText(row[ci])),
  );

  // Natural width: the widest of header and body content. Content only — the
  // gutter is reserved separately by availableWidth, not folded in here.
  const naturals = columns.map((c, ci) => {
    let w = measureTracked(ctx.fonts[headSpec.font], headerDisplay[ci], headSpec.size, headSpec.tracking);
    for (const row of bodyDisplay) {
      const cw = measureTracked(ctx.fonts[bodySpec.font], row[ci], columns[ci].fontSize || bodySpec.size, bodySpec.tracking);
      if (cw > w) w = cw;
    }
    return w;
  });

  const widths = resolveWidths(naturals, columns.map((c) => c.widthHint), warnings);

  // x steps by width + gutter, so gaps sit between columns and the last
  // column's right edge lands on MARGIN_LEFT + CONTENT_WIDTH.
  let x = MARGIN_LEFT;
  const resolved = columns.map((c, i) => {
    const col = { ...c, width: widths[i], x };
    x += widths[i] + COLUMN_GUTTER;
    return col;
  });

  // Wrapping, then heights. §1.3: row height 18 pt minimum, growing with content.
  const headerLines = resolved.map((c, ci) =>
    wrapTracked(ctx.fonts[headSpec.font], headerDisplay[ci], headSpec.size, c.width, headSpec.tracking),
  );
  // Optional extra space under the text of every header and body row, so the
  // last wrapped line never sits on the rule beneath it. Not a §1.4 value:
  // defaults to 0, so proposals are unchanged; the handoff PDF passes a few pt.
  const cellPadding = Number.isFinite(table.cellPadding) && table.cellPadding > 0 ? table.cellPadding : 0;

  const headerHeight = Math.max(
    MIN_ROW_HEIGHT,
    Math.max(...headerLines.map((l) => l.length)) * BODY_LEADING,
  ) + cellPadding;

  const rowLines = bodyDisplay.map((row) =>
    resolved.map((c, ci) =>
      wrapTracked(ctx.fonts[bodySpec.font], row[ci], c.fontSize || bodySpec.size, c.width, bodySpec.tracking),
    ),
  );
  const rowHeights = rowLines.map((cells) =>
    Math.max(MIN_ROW_HEIGHT, Math.max(...cells.map((l) => l.length)) * BODY_LEADING) + cellPadding,
  );

  const titleHeight = BASELINE + TITLE_GAP;
  const nRows = rowHeights.length;
  const ruleHeight =
    HEADER_RULE_THICKNESS +
    Math.max(0, nRows - 1) * HAIRLINE_THICKNESS +
    CLOSING_RULE_THICKNESS;

  const plan = {
    title: applyTransform(table.title, titleSpec.transform),
    columns: resolved,
    headerDisplay,
    headerLines,
    headerHeight,
    bodyDisplay,
    rowLines,
    rowHeights,
    titleHeight,
    ruleHeight,
    totalHeight:
      titleHeight + headerHeight + rowHeights.reduce((a, b) => a + b, 0) + ruleHeight,
    splitPoints: legalSplitCounts(nRows),
    warnings,
  };

  return plan;
}

/** Draw one row of cells at `top`, returning the y below it. */
function drawRow(ctx, page, plan, lines, spec, top, color) {
  plan.columns.forEach((col, ci) => {
    const cellLines = lines[ci];
    cellLines.forEach((line, li) => {
      const baseline = top - TEXT_INSET - li * BODY_LEADING;
      drawTracked(page, line, {
        x: col.align === 'right' ? col.x + col.width : col.x,
        y: baseline,
        font: ctx.fonts[spec.font],
        // Body rows honour a per-column fontSize; the header row never does.
        size: spec === TYPE.tableBodyCell && col.fontSize ? col.fontSize : spec.size,
        color,
        tracking: spec.tracking,
        align: col.align === 'right' ? 'right' : 'left',
        overflow: ctx.overflow(),
      });
    });
  });
}

function drawRule(page, y, thickness, color) {
  page.drawLine({
    start: { x: MARGIN_LEFT, y },
    end: { x: RIGHT_EDGE, y },
    thickness,
    color,
  });
}

/**
 * Draw one fragment: optional title, header row, header rule, `count` body
 * rows separated by hairlines, closing rule. Returns the y below the fragment.
 */
function drawFragment(ctx, plan, page, start, count, withTitle) {
  const titleSpec = TYPE.tableTitle;
  const headSpec = TYPE.tableHeaderCell;
  const bodySpec = TYPE.tableBodyCell;
  let y = ctx.cursor;

  if (withTitle) {
    // §1.4 rule 8. §1.2 assigns no colour to the table title; body text it is.
    drawTracked(page, plan.title, {
      x: MARGIN_LEFT,
      y: y - BASELINE,
      font: ctx.fonts[titleSpec.font],
      size: titleSpec.size,
      color: ctx.colors.text,
      tracking: titleSpec.tracking,
      overflow: ctx.overflow(),
    });
    y -= plan.titleHeight;
  }

  drawRow(ctx, page, plan, plan.headerLines, headSpec, y, ctx.colors.text);
  y -= plan.headerHeight;

  // §1.4 rule 2: one 0.75 pt accent rule under the header row.
  drawRule(page, y, HEADER_RULE_THICKNESS, ctx.colors.accent);
  y -= HEADER_RULE_THICKNESS;

  for (let i = 0; i < count; i++) {
    const idx = start + i;
    drawRow(ctx, page, plan, plan.rowLines[idx], bodySpec, y, ctx.colors.text);
    y -= plan.rowHeights[idx];

    // 0.25 pt hairline BETWEEN body rows — never after the last, which the
    // closing rule terminates. No zebra striping (rule 3), no cell borders
    // and no vertical rules (rules 1 and 2).
    if (i < count - 1) {
      drawRule(page, y, HAIRLINE_THICKNESS, ctx.colors.hairline);
      y -= HAIRLINE_THICKNESS;
    }
  }

  // §1.4 rule 2: one 0.75 pt accent rule closing the table.
  drawRule(page, y, CLOSING_RULE_THICKNESS, ctx.colors.accent);
  y -= CLOSING_RULE_THICKNESS;

  return y;
}

/**
 * Draw a measured table, paging per §1.4 rule 9.
 *
 * A table that cannot fit but has no legal split moves whole to a fresh page.
 * A table that can split does so only where both fragments keep two or more
 * rows. Continuation fragments repeat the header row and its accent rule but
 * not the title — §1.4 is silent on continuation furniture, so this is the
 * module's reading, not a quoted rule.
 *
 * @param {Object} ctx
 * @param {Object} table
 * @param {Object} plan from measureTable
 * @returns {number} the new cursor y, also written back to ctx.cursor
 */
export function drawTable(ctx, table, plan) {
  const nRows = plan.rowHeights.length;
  let start = 0;
  let withTitle = true;
  let movedForThisFragment = false;

  if (ctx.pages.length === 0) ctx.newPage();

  for (;;) {
    const page = ctx.pages[ctx.pages.length - 1];
    const remaining = nRows - start;
    const avail = ctx.cursor - CONTENT_FLOOR;

    if (fragmentHeight(plan, start, remaining, withTitle) <= avail) {
      ctx.cursor = drawFragment(ctx, plan, page, start, remaining, withTitle);
      return ctx.cursor;
    }

    // Largest legal fragment that fits, per rule 9.
    let chosen = 0;
    for (const k of legalSplitCounts(remaining)) {
      if (fragmentHeight(plan, start, k, withTitle) <= avail) chosen = k;
    }

    if (chosen === 0) {
      if (!movedForThisFragment) {
        // Nothing legal fits here — move the whole (remaining) table.
        ctx.newPage();
        movedForThisFragment = true;
        continue;
      }
      // Already on a fresh page and still short: the table is taller than a
      // page. Draw it and let it overflow rather than loop forever.
      plan.warnings.push(
        `table fragment from row ${start} exceeds a full page; drawn without a legal split`,
      );
      ctx.cursor = drawFragment(ctx, plan, page, start, remaining, withTitle);
      return ctx.cursor;
    }

    ctx.cursor = drawFragment(ctx, plan, page, start, chosen, withTitle);
    start += chosen;
    withTitle = false;
    movedForThisFragment = false;
    ctx.newPage();
  }
}
